import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Gauge } from "lucide-react";
import { useShopStatus } from "@/lib/shopStatus";

const LINKS = [
  { label: "Acasă", href: "#acasa" },
  { label: "WhatsApp", href: "#whatsapp" },
  { label: "Recenzii", href: "#recenzii" },
  { label: "Program", href: "#program" },
  { label: "Contact", href: "#contact" },
];

const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const StatusBadge = ({ compact = false }) => {
  const s = useShopStatus();
  const color = s.isOpen ? (s.closingSoon ? "text-amber-400" : "text-emerald-400") : "text-[#FF2A3B]";
  const label = s.isOpen ? (s.closingSoon ? "Se închide curând" : "Deschis acum") : "Închis acum";
  return (
    <div data-testid="shop-status-badge" className="flex items-center gap-2">
      <span className={`relative inline-flex h-2 w-2 rounded-full bg-current status-ping ${color}`} />
      <span className={`text-[11px] font-mono uppercase tracking-[0.18em] ${color}`}>
        {label}
        {!compact && s.isOpen && <span className="text-slate-500"> · până la {s.today.close}</span>}
        {!compact && !s.isOpen && s.nextOpenLabel && <span className="text-slate-500"> · {s.nextOpenLabel}</span>}
      </span>
    </div>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#07122B]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <button
          data-testid="logo-home-link"
          onClick={() => scrollTo("#acasa")}
          className="flex items-center gap-3 group"
        >
          <span className="relative grid place-items-center h-10 w-10 border border-white/15 bg-[#131927] group-hover:border-[#0055FF] transition-colors">
            <Gauge className="h-5 w-5 text-[#0055FF] group-hover:text-[#FF2A3B] transition-colors" />
            <span className="absolute -bottom-px -right-px h-2 w-2 bg-[#FF2A3B]" />
          </span>
          <span className="text-left leading-none">
            <span className="block font-display font-800 font-extrabold tracking-tight text-sm uppercase">
              Speed Shop <span className="text-[#FF2A3B]">Power</span>
            </span>
            <span className="block text-[10px] font-mono tracking-[0.28em] text-slate-500 uppercase mt-1">
              Piese auto · Timișoara
            </span>
          </span>
        </button>

        <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace("ă", "a")}`}
              onClick={() => scrollTo(l.href)}
              className="text-[13px] font-medium uppercase tracking-[0.14em] text-slate-400 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <StatusBadge compact />
          <a
            data-testid="header-phone-cta"
            href="tel:+40786804728"
            className="flex items-center gap-2 bg-[#0055FF] hover:bg-[#FF2A3B] text-white text-sm font-semibold px-4 py-2.5 transition-colors duration-300"
          >
            <Phone className="h-4 w-4" />
            0786 804 728
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            data-testid="header-mobile-call"
            href="tel:+40786804728"
            aria-label="Sună acum"
            className="grid place-items-center h-10 w-10 border border-[#FF2A3B]/50 bg-[#FF2A3B]/10 text-[#FF2A3B]"
          >
            <Phone className="h-5 w-5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-white p-2"
            aria-label="Meniu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#07122B] border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace("ă", "a")}`}
                  onClick={() => { setOpen(false); scrollTo(l.href); }}
                  className="text-left font-display text-2xl font-bold uppercase tracking-tight text-slate-300 hover:text-[#FF2A3B] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a data-testid="mobile-phone-cta" href="tel:+40786804728" className="mt-2 flex items-center justify-center gap-2 bg-[#0055FF] py-3 font-semibold">
                <Phone className="h-4 w-4" /> 0786 804 728
              </a>
              <StatusBadge />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
